import { cva } from 'class-variance-authority';

/**
 * Badge class recipe.
 *
 * Ported from the Figma component set (file 5aSkE0U4TeWbuIZDvccHjc, node 600:43878), whose 8 cells
 * are Appearance (Count / Dot) x Type (Neutral / Accent / Destructive / Success). That is the full
 * cartesian product — every combination is drawn, nothing is hidden, and the set has NO State axis
 * at all, because a Badge is never interactive. So unlike every other recipe in this directory
 * there are no hover, pressed, focus or disabled reads below: the component has exactly eight
 * appearances and this file spells all eight.
 *
 * Like `Button.recipe` and `Tag.recipe` this contains ZERO `dark:` variants. Colour comes entirely
 * from the `--ds-badge-*` slots, which the `.ds-badge-<colour>` classes in
 * `components/styles/design-system.css` set from the DS 2.0 semantic roles. Three of the four
 * Types are theme-invariant in the library too; `neutral` is the one that flips, and it flips for
 * free because both roles it names already do.
 *
 * Geometry, read off the set rather than inferred:
 *
 *              width            height   padding   radius
 *   Count      min 20, hugs     20       0 / 4     full
 *   Dot        8 (fixed)        8        —         full
 *
 * NO BORDER on any cell (`strokes: []` in all eight), which is worth stating because it makes this
 * the one DS 2.0 shape immune to the inside-stroke arithmetic every other port has to reason
 * about: there is no 1px to be inside or outside the box, so 20px in Figma is 20px in CSS.
 *
 * The Count's 20px height is the LINE BOX, not a declared height — the set hugs vertically with
 * zero vertical padding around a 12px/20px label. `h-5` states the same number as a height so the
 * box cannot collapse if the type scale is ever changed underneath it; `min-w-5` is the file's own
 * `minWidth` binding (`x5`), which is what makes one digit a circle and three characters stretch
 * into a pill. Two digits are the interesting case and the note below is about exactly that.
 *
 * The authored radius is `radius/full`, which clamps to fully-rounded at both 20px and 8px, so
 * `rounded-full` is not an approximation.
 *
 * PROPORTIONAL DIGITS, reproducing the drawing — and the docs page's own two claims about width
 * cannot both be true in this typeface, which is the one finding in this port worth reading twice.
 *
 * The page says two digits "fill the same 20px, so a row of badges does not jitter as counts tick
 * over". That is TWO claims, and Google Sans Flex can satisfy either but not both. Measured in
 * Chromium at 12px/500 (2026-09-17), glyph advance only, before padding:
 *
 *              proportional   tabular
 *   "1"                4.64      7.81
 *   "12"              11.13     15.63
 *   "48"              14.11     15.63
 *   "99+"             20.52     23.44
 *
 * Add the 8px of horizontal padding and clamp at the 20px minimum. Proportional gives `12` -> 20,
 * the drawn circle, and `48` -> 22, so a count changing digit SHAPE shifts its row by about 2px.
 * The face does have a tabular set and it removes that shift entirely — but its advance is WIDER,
 * so two digits become 23.6px and no two-digit badge is ever the 20px the file draws.
 *
 * So the drawing wins. A permanent 3.6px deviation from the authored cell at the most common size
 * is a worse trade than a ~2px shift that only happens when a digit changes shape, and matching
 * the authored cell is this port's job. Reported with the numbers rather than resolved here: the
 * no-jitter sentence is an aspiration this typeface cannot meet at a 20px minimum, and whether the
 * minimum or the promise moves is a designer's call.
 *
 * `Geometry` pins both halves of it — that one digit clamps to the minimum, and that `48` really
 * is wider than `12` — so the trade-off is asserted rather than merely described.
 */
export const badgeRecipe = cva(
  [
    'ds-badge',
    'inline-flex shrink-0 items-center justify-center',
    'rounded-full',

    // Slot reads. The `.ds-badge-*` classes supply the values.
    //
    // TWO SLOTS ONLY, and that is the whole palette. A Badge is a solid fill with a foreground on
    // it — no border, no icon, no hover step, no disabled family, because the set draws none of
    // them. `--ds-badge-fg` does nothing on the `dot` appearance, which has no content; every
    // colour class still sets it, because a class whose slot set varied by appearance would be a
    // cell the contrast harness could not enumerate.
    'bg-[var(--ds-badge-bg)] text-[var(--ds-badge-fg)]',
  ],
  {
    variants: {
      appearance: {
        count: [
          'h-5 min-w-5 px-1',
          'font-sans text-label-md',
          // The set's text style is Google Sans Flex Medium 12/20 with no tracking, which
          // `text-label-md` matches exactly (12px / 20px / 500).
          'text-center',
        ],
        dot: 'size-2',
      },
      color: {
        neutral: 'ds-badge-neutral',
        primary: 'ds-badge-primary',
        success: 'ds-badge-success',
        destructive: 'ds-badge-destructive',
      },
    },
    defaultVariants: { appearance: 'count', color: 'neutral' },
  }
);
